// Minimal "Talk to a human" affordance. Demo-grade: posts to /livechat/customer/escalate
// and surfaces success / failure inline. Behind the LiveChatCustomerEscalateEnabled flag
// so it can ship dark and be flipped on per environment. The full customer UX (inline
// prompt rendering, presence-aware availability gating) is tracked separately by
// issues #1589 and #1599.
import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { PersonSupport20Regular } from '@fluentui/react-icons';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { ConflictError, NotFoundError } from '../../libs/services/BaseService';
import { CustomerLiveChatService, EscalateConflictReasons } from '../../libs/services/CustomerLiveChatService';
import { isHandoverEligible } from '../../libs/utils/handoverEligibility';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';

const useClasses = makeStyles({
    root: {
        display: 'inline-flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
    status: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
    error: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorPaletteRedForeground1,
    },
});

type State = { kind: 'idle' } | { kind: 'pending' } | { kind: 'queued' } | { kind: 'error'; message: string };

// How often, while queued, we re-read the chat's mode as a fallback for a dropped terminal notice.
// Low frequency — this is a robustness backstop, not the primary dismissal path (a delivered
// AgentJoined / QueueTimedOut notice clears the transcript-side UI immediately); a few seconds of
// lag clearing the local hint is fine, and the call only runs while the customer is queued.
const MODE_POLL_INTERVAL_MS = 5000;

export const EscalateToHumanButton: FC = () => {
    const classes = useClasses();
    const { t } = useTranslation();
    const flagOn = useAppSelector(
        (state: RootState) => state.app.serviceInfo.featureFlags.LiveChatCustomerEscalateEnabled,
    );
    const chatId = useAppSelector((state: RootState) => state.conversations.selectedId);
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const embeddedMode = useAppSelector((state: RootState) => state.app.embeddedMode);
    // hostContext is forwarded as a request header but must not be an effect trigger: the slice is
    // merged on every host page-context update (path/title), so a new object identity each time
    // would needlessly restart the mode poll. A ref hands the poll the latest value without re-running.
    const hostContextRef = useRef(hostContext);
    hostContextRef.current = hostContext;
    // Mirror the server-side customer-group gate so the button isn't shown when an escalate would
    // 404. A boolean (not the object) so it only flips the effects when eligibility actually changes.
    const eligible = useMemo(() => isHandoverEligible(hostContext), [hostContext]);
    // Two-step (not conversations[chatId].messages) so a selectedId with no loaded conversation
    // yields undefined instead of throwing at render.
    const conversation = useAppSelector((state: RootState) =>
        chatId ? state.conversations.conversations[chatId] : undefined,
    );
    const messages = conversation?.messages;
    const auth = useAuth();
    const token = auth.user?.access_token;
    const [state, setState] = useState<State>({ kind: 'idle' });
    // null = probe in flight; the button stays hidden until we confirm the user is an allow-listed
    // agent, mirroring the server-side gate on the manual escalate route.
    const [isAllowedAgent, setIsAllowedAgent] = useState<boolean | null>(null);
    // Baseline so the dismissal effect only reacts to notices after this escalation, not a prior
    // cycle's still in the transcript. null = transcript wasn't loaded at escalate time; the effect
    // anchors it once messages arrive so a yet-to-load prior notice can't dismiss a live queue.
    const queuedBaselineRef = useRef<number | null>(null);

    // Reset on chat change so a queued / error state from chat A doesn't leak into
    // chat B (button stuck disabled, stale "An agent will join shortly." text on a
    // completely unrelated chat). The component instance survives Redux's
    // selectedId change; without this, only a remount would clear it.
    useEffect(() => {
        setState({ kind: 'idle' });
    }, [chatId]);

    // Probe agent membership once a token is available, but only in standalone and only when the
    // customer-group gate would pass — no point calling the agent endpoint for a context the manual
    // escalate would 404 anyway, which also keeps the probe off ineligible/embedded sessions.
    useEffect(() => {
        if (embeddedMode || !flagOn || !token || !eligible) {
            return;
        }
        let active = true;
        const service = new CustomerLiveChatService(token);
        void service.isAllowedAgentAsync().then((allowed) => {
            if (active) {
                setIsAllowedAgent(allowed);
            }
        });
        return () => {
            active = false;
        };
    }, [embeddedMode, flagOn, token, eligible]);

    // Primary dismissal: drop the waiting hint once a terminal System notice lands in the
    // transcript (a human joined, the chat resolved, or the queue timed out).
    useEffect(() => {
        if (state.kind !== 'queued' || !messages) {
            return;
        }
        // Transcript loaded only after we queued — anchor the baseline against it now (a prior
        // cycle's notices sit below this point and so can't dismiss the live queue).
        if (queuedBaselineRef.current === null) {
            queuedBaselineRef.current = messages.length;
            return;
        }
        const leftQueue = messages
            .slice(queuedBaselineRef.current)
            .some(
                (m) =>
                    m.systemNotice === 'AgentJoined' ||
                    m.systemNotice === 'ChatResolved' ||
                    m.systemNotice === 'QueueTimedOut',
            );
        if (leftQueue) {
            setState({ kind: 'idle' });
        }
    }, [messages, state.kind]);

    // Source-of-truth fallback (#1882). The terminal notice above is best-effort — its persist /
    // broadcast can be dropped on a transient Cosmos/SignalR failure (the queue-timeout notice is
    // sent after the chat is already Resolved and logs-and-swallows), leaving the customer stranded
    // on "Waiting for a human…" while the AI silently answers again. While queued we also poll the
    // chat's mode and reset to idle as soon as it's no longer Queued — clearing the hint even if the
    // notice never landed and even if the customer never sends another message. Only runs while
    // queued; stops on dismiss, chat change, token change, and unmount.
    useEffect(() => {
        if (state.kind !== 'queued' || !chatId || !token) {
            return;
        }

        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout>;
        const service = new CustomerLiveChatService(token);
        const poll = async () => {
            let stillQueued = true;
            try {
                const { mode } = await service.getModeAsync(chatId, hostContextRef.current);
                // Only act on a definitively-known non-Queued mode. A failed/empty read leaves the
                // hint in place — never dismiss on uncertainty, that would reintroduce the stranding
                // bug. The allowlist (rather than `mode !== 'Queued'`) guards the empty-body case:
                // BaseService maps a 429 to `{}` (not a throw, so the catch below never fires), so
                // `mode` is undefined at runtime even though the type says otherwise — and a bare
                // `!== 'Queued'` would treat that as non-Queued and falsely clear the hint while the
                // customer is still genuinely queued.
                if (mode === 'Human' || mode === 'Resolved' || mode === 'AI') {
                    stillQueued = false;
                    if (!cancelled) {
                        setState({ kind: 'idle' });
                    }
                }
            } catch {
                // Transient read failure (network blip, 404 from a flag flipped mid-session): swallow
                // and retry on the next tick. The whole point is robustness, so a single bad poll
                // must not clear the hint.
            }
            // Recursive setTimeout rather than setInterval so the next read is scheduled only after
            // this one settles — a slow/laggy request can never pile overlapping polls. Stop once
            // we've left the queue (or the effect was torn down).
            if (!cancelled && stillQueued) {
                timeoutId = setTimeout(() => void poll(), MODE_POLL_INTERVAL_MS);
            }
        };
        timeoutId = setTimeout(() => void poll(), MODE_POLL_INTERVAL_MS);

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [state.kind, chatId, token]);

    // Hide while the access token isn't yet available (initial load, silent renew).
    // Without this, clicking the button would POST with an empty Authorization
    // header and surface a misleading "Could not request a human." error to the user.
    // Also hidden embedded, when the customer-group gate would reject (so we never show a button
    // that 404s), and until the agent probe confirms the user is allow-listed (isAllowedAgent ===
    // true) — non-agents and the in-flight state stay hidden.
    if (embeddedMode || !flagOn || !chatId || !token || !eligible || isAllowedAgent !== true) {
        return null;
    }

    const escalate = async () => {
        setState({ kind: 'pending' });
        try {
            // Construct on click rather than per render — the service is stateless,
            // only used when the user actually clicks, and capturing `token` at click
            // time picks up the freshest value after a silent renew.
            const service = new CustomerLiveChatService(token);
            await service.escalateAsync(chatId, hostContext);
            queuedBaselineRef.current = messages?.length ?? null;
            setState({ kind: 'queued' });
        } catch (err) {
            // Only already_queued/already_handled mean the chat is already in progress; the transient
            // 409s (queue_full, concurrently_modified) and unknowns fall through to the generic
            // retryable message rather than a misleading "already queued" one.
            if (err instanceof ConflictError) {
                let message: string;
                if (err.reason === EscalateConflictReasons.NoAgentsOnline) {
                    message = t('escalationOffer.errorNoAgents');
                } else if (
                    err.reason === EscalateConflictReasons.AlreadyQueued ||
                    err.reason === EscalateConflictReasons.AlreadyHandled
                ) {
                    message = t('escalateButton.errorAlreadyActive');
                } else {
                    message = t('escalateButton.errorGeneric');
                }
                setState({ kind: 'error', message });
            } else if (err instanceof NotFoundError) {
                setState({ kind: 'error', message: t('escalateButton.errorNotFound') });
            } else {
                setState({ kind: 'error', message: t('escalateButton.errorGeneric') });
            }
        }
    };

    const label = state.kind === 'queued' ? t('escalateButton.waitingLabel') : t('escalateButton.label');
    return (
        <div className={classes.root}>
            <Button
                appearance="subtle"
                icon={<PersonSupport20Regular />}
                onClick={() => {
                    void escalate();
                }}
                disabled={state.kind === 'pending' || state.kind === 'queued'}
                aria-label={label}
                title={label}
                data-testid="escalate-to-human-button"
            />
            {state.kind === 'error' && <span className={classes.error}>{state.message}</span>}
            {state.kind === 'queued' && <span className={classes.status}>{t('escalationOffer.waiting')}</span>}
        </div>
    );
};
